import reactUpdate from 'immutability-helper';

import {EventConstants} from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {Comment, FromServerProtocol} from 'notebook/ELNConstants';
import TableUtil from 'notebook/tables/TableUtil';
import notifications from 'notifications';
import {ArrayElement} from 'util/Types';

class ProtocolStore extends Store {
  protocol!: FromServerProtocol | null;
  protocolID!: string | null;

  resetState(): void {
    this.protocol = null;
    this.protocolID = null;
  }

  getState(): {protocol: FromServerProtocol | null} {
    return {protocol: this.protocol};
  }

  _set(protocol: FromServerProtocol): void {
    this.protocol = this.prepareForStore(protocol);
  }

  _merge(data: Partial<FromServerProtocol>): void {
    this.protocol = reactUpdate(this.protocol, {$merge: data});
  }

  _addComment(comment: Comment): void {
    this.protocol = reactUpdate(this.protocol, {comments: {$push: [comment]}});
  }

  _deleteComment(commentID: number): void {
    const {comments} = this.protocol!;
    this.protocol = reactUpdate(this.protocol, {
      comments: {$set: comments.filter((c) => c.id !== commentID)},
    });
  }

  _loadedAndAffected(protocolID: string): boolean {
    return this.protocol != null && protocolID === this.protocolID;
  }

  _apply(protocolID: string, protocolFunc: Function): void {
    let shouldEmit = false;
    if (this._loadedAndAffected(protocolID)) {
      protocolFunc.call(this);
      shouldEmit = true;
    }
    if (shouldEmit) {
      this.emitChange();
    }
  }

  payloadHandler(action: FluxAction): boolean {
    let comment: Comment;
    let commentID: number;
    let data: {tagSchemaId: string | null};
    let error: Response;
    let newSteps: FromServerProtocol['steps'];
    let protocol: FromServerProtocol;
    let protocolID: string;
    let stepID: number;
    let step: ArrayElement<FromServerProtocol['steps']>;

    switch (action.actionType) {
      // OPEN
      case EventConstants.PROTOCOL_OPEN:
        ({protocolID} = action);
        this.protocolID = protocolID;
        this.emitChange();
        break;

      case EventConstants.PROTOCOL_OPEN_COMPLETED:
        ({protocolID, protocol} = action);
        if (protocolID === this.protocolID) {
          this._set(protocol);
          this.emitChange();
        }
        break;

      case EventConstants.PROTOCOL_OPEN_FAILED:
        ({protocolID} = action);
        if (this._loadedAndAffected(protocolID)) {
          this.emitChange({
            action,
            error: true,
          });
        }
        break;

      // UPDATE
      case EventConstants.PROTOCOL_UPDATE:
        break; // Nothing yet.

      case EventConstants.PROTOCOL_UPDATE_COMPLETED:
        ({protocolID, data} = action);
        this._apply(protocolID, function () {
          // @ts-expect-error 'this' implicitly has type 'any' because it does not have a type annotation
          return this._merge(data);
        });
        break;

      case EventConstants.PROTOCOL_UPDATE_FAILED:
        ({protocolID, error} = action);
        notifications.notifyError(error);
        if (this._loadedAndAffected(protocolID)) {
          this.emitChange({
            action,
            error: true,
          });
        }
        break;

      // DELETE
      case EventConstants.PROTOCOL_DELETE:
        break;

      case EventConstants.PROTOCOL_DELETE_COMPLETED:
        ({protocolID} = action);
        this._apply(protocolID, function () {
          // @ts-expect-error 'this' implicitly has type 'any' because it does not have a type annotation
          return this.resetState();
        });
        break;

      // COMMENT
      case EventConstants.PROTOCOL_COMMENT:
        break;

      case EventConstants.PROTOCOL_COMMENT_COMPLETED:
        ({protocolID, comment} = action);
        if (this._loadedAndAffected(protocolID)) {
          this._addComment(comment);
          this.emitChange();
        }
        break;

      case EventConstants.PROTOCOL_DELETE_COMMENT:
        break;

      case EventConstants.PROTOCOL_DELETE_COMMENT_COMPLETED:
        ({protocolID, commentID} = action);
        if (this._loadedAndAffected(protocolID)) {
          this._deleteComment(commentID);
          this.emitChange();
        }
        break;

      case EventConstants.PROTOCOL_STEP_COMPLETE_COMPLETED:
        ({protocolID, stepID} = action);
        if (this._loadedAndAffected(protocolID)) {
          newSteps = (() => {
            const result = [];
            for (step of Array.from(this.protocol!.steps)) {
              if (step.id !== stepID) {
                result.push(step);
              } else {
                result.push(reactUpdate(step, {$merge: {completed: true}}));
              }
            }
            return result;
          })();
          this.protocol = reactUpdate(this.protocol, {$merge: {steps: newSteps}});
          this.emitChange();
        }
        break;

      case EventConstants.PROTOCOL_STEP_RESET_COMPLETED:
        ({protocolID, stepID} = action);
        if (this._loadedAndAffected(protocolID)) {
          newSteps = (() => {
            const result1 = [];
            for (step of Array.from(this.protocol!.steps)) {
              if (step.id !== stepID) {
                result1.push(step);
              } else {
                result1.push(reactUpdate(step, {$merge: {completed: false}}));
              }
            }
            return result1;
          })();
          this.protocol = reactUpdate(this.protocol, {$merge: {steps: newSteps}});
          this.emitChange();
        }
        break;

      case EventConstants.PROTOCOL_RESET_STEPS_COMPLETED:
        ({protocolID} = action);
        if (this._loadedAndAffected(protocolID)) {
          newSteps = (() => {
            const result2 = [];
            for (step of Array.from(this.protocol!.steps)) {
              result2.push(reactUpdate(step, {$merge: {completed: false}}));
            }
            return result2;
          })();
          this.protocol = reactUpdate(this.protocol, {$merge: {steps: newSteps}});
          this.emitChange();
        }
        break;

      case EventConstants.PROTOCOL_STEP_COMPLETE_FAILED:
        ({protocolID, stepID} = action);
        if (this._loadedAndAffected(protocolID)) {
          newSteps = (() => {
            const result3 = [];
            for (step of Array.from(this.protocol!.steps)) {
              if (step.id !== stepID) {
                result3.push(step);
              } else {
                result3.push(reactUpdate(step, {$merge: {completed: false}}));
              }
            }
            return result3;
          })();
          this.protocol = reactUpdate(this.protocol, {$merge: {steps: newSteps}});
          this.emitChange();
        }
        notifications.error('An error occurred while marking step complete.');
        break;

      case EventConstants.PROTOCOL_STEP_RESET_FAILED:
        ({protocolID, stepID} = action);
        if (this._loadedAndAffected(protocolID)) {
          newSteps = (() => {
            const result4 = [];
            for (step of Array.from(this.protocol!.steps)) {
              if (step.id !== stepID) {
                result4.push(step);
              } else {
                result4.push(reactUpdate(step, {$merge: {completed: true}}));
              }
            }
            return result4;
          })();
          this.protocol = reactUpdate(this.protocol, {$merge: {steps: newSteps}});
          this.emitChange();
        }
        notifications.error('An error occurred while un-marking step complete.');
        break;

      case EventConstants.PROTOCOL_RESET_STEPS_FAILED:
        notifications.error('An error occurred while clearing step progress.');
        break;

      case EventConstants.PROTOCOL_DELETE_FAILED:
        ({protocolID, error} = action);
        if (this._loadedAndAffected(protocolID)) {
          notifications.notifyError(error);
        }
        break;

      // Misc. failure handles
      case EventConstants.PROTOCOL_COMMENT_FAILED:
      case EventConstants.PROTOCOL_DELETE_COMMENT_FAILED:
        ({protocolID, error} = action);
        if (this._loadedAndAffected(protocolID)) {
          this.emitChange({
            action,
            error: true,
          });
        }
        break;

      default:
        break;
    }

    return true;
  }

  prepareForStore(protocol: FromServerProtocol): FromServerProtocol {
    protocol.steps.forEach((step) => {
      if (step.table != null) {
        step.table = TableUtil.prepareForStore(step.table);
      }
    });
    return protocol;
  }

  __serverBootstrap(data: {protocol: FromServerProtocol}): {} | void {
    if (data?.protocol != null) {
      data.protocol = this.prepareForStore(data.protocol);
    }
    return super.__serverBootstrap(data);
  }
}

export default new ProtocolStore(AppDispatcher);
